// Base API configuration
// Normalize and validate base URL to ensure absolute URLs in production
const normalizeBaseUrl = (rawUrl) => {
  let trimmed = String(rawUrl).trim();
  if (trimmed.endsWith('/')) {
    trimmed = trimmed.slice(0, -1);
  }
  // If protocol missing, default to https in prod, http in dev
  if (!/^https?:\/\//i.test(trimmed)) {
    trimmed = (import.meta.env.PROD ? 'https://' : 'http://') + trimmed;
  }
  return trimmed;
};

const API_BASE_URL = normalizeBaseUrl(
  import.meta.env.VITE_API_URL || (import.meta.env.PROD ? 'https://api.hoabuddy.org' : 'http://localhost:3001')
);
console.log('API_BASE_URL:', API_BASE_URL);
const API_TIMEOUT = 10000;

// Store for access token (will be set by auth system)
let currentAccessToken = null;

// Function to set access token (called by auth system)
export const setAccessToken = (token) => {
  currentAccessToken = token;
};

// Function to get access token
export const getAccessToken = () => {
  return currentAccessToken;
};

// Store for auth context (to call refresh function)
let authContext = null;

// Function to set auth context (called by useAuth hook)
export const setAuthContext = (context) => {
  authContext = context;
};

// Helper function to handle API requests with common logic
const apiRequest = async (endpoint, options = {}) => {
  const url = new URL(endpoint, API_BASE_URL).toString();
  // Default headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Add access token if available
  if (currentAccessToken) {
    headers.Authorization = `Bearer ${currentAccessToken}`;
  }
  
  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
      credentials: 'include', // Include cookies for refresh token
    });
    
    clearTimeout(timeoutId);
    
    // Handle 401 Unauthorized - try to refresh token
    // Don't retry via refreshAccessToken if the failing request IS the refresh endpoint —
    // that creates a self-referential promise deadlock (the promise awaits itself).
    const isRefreshEndpoint = url.includes('/api/auth/refresh');
    if (response.status === 401 && !isRefreshEndpoint && authContext?.refreshAccessToken) {
      try {
        // Try to refresh the token
        const newToken = await authContext.refreshAccessToken();
        
        // Retry the original request with new token
        if (newToken) {
          const retryHeaders = {
            ...headers,
            Authorization: `Bearer ${newToken}`
          };
          
          const retryResponse = await fetch(url, {
            ...options,
            headers: retryHeaders,
            credentials: 'include',
          });
          
          if (retryResponse.ok) {
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await retryResponse.json();
            } else if (retryResponse.status === 204) {
              return null;
            } else {
              return retryResponse;
            }
          } else {
            // If retry also fails, handle it normally
            const errorData = await retryResponse.json().catch(() => ({}));
            const error = new Error(errorData.message || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`);
            error.errors = errorData.errors;
            error.status = retryResponse.status;
            throw error;
          }
        }
      } catch (refreshError) {
        console.log('Token refresh failed during API call:', refreshError.message);
        // Clear auth state if refresh fails
        if (authContext?.logout) {
          authContext.logout();
        }
        // Don't force redirect here - let React Router handle it
        throw new Error('Session expired');
      }
    }
    
    // Handle other HTTP errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      // Preserve the entire error response and HTTP details
      error.response = {
        data: errorData,
        status: response.status,
        statusText: response.statusText
      };
      throw error;
    }
    
    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else if (response.status === 204) {
      return null; // No content
    } else {
      return response; // Return raw response for files, etc.
    }
    
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
};

// HTTP method helpers
export const api = {
  get: (endpoint, params = {}) => {
    const url = new URL(endpoint, API_BASE_URL);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });
    return apiRequest(url.toString());
  },
  
  post: (endpoint, data = {}) => {
    return apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  put: (endpoint, data = {}) => {
    return apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  delete: (endpoint) => {
    return apiRequest(endpoint, {
      method: 'DELETE',
    });
  },
  
  // For file uploads
  postFormData: (endpoint, formData) => {
    return apiRequest(endpoint, {
      method: 'POST',
      body: formData,
      headers: {}, // Don't set Content-Type for FormData
    });
  },
  
  // For file downloads
  getBlob: async (endpoint) => {
    const response = await apiRequest(endpoint, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.blob();
  },
}; 